import axios, { AxiosError } from 'axios';
import { LayoutContext } from 'contexts/layoutContext/LayoutContext';
import {
  ApiErrorCode,
  Ferramenta,
  ModalidadeParticipante,
  RotasComBarra,
} from 'enum';
import { AssociadoType } from 'models/associado';
import {
  DadosNaoAssociado,
  Evento,
  ParticipanteNaoAssociado,
} from 'models/naoAssociado';
import {
  createContext,
  useCallback,
  useContext,
  useReducer,
  useState,
} from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useNavigate } from 'react-router-dom';
import {
  ApiErrorType,
  buscarCPF,
  confirmarPresencaAssociado,
  obterDadosEvento,
  salvarParticipanteNaoAssociado,
} from 'services/Api';
import RegistroReducer, { RegistroContextType } from './RegistroReducer';
import { languageValues } from 'utils/LanguageUtils';
import { getErrorMessages, showToastErrors } from 'utils/ErrorUtils';

const { agradecimento, presenca, agradecimentoNaoAssociado } = RotasComBarra;

export interface RegistroReducerType {
  flow: boolean;
  ferramenta?: Ferramenta;
  hibrido: boolean;
  loading: boolean;
  idEvento?: number;
  evento?: Evento;
  dados?: AssociadoType;
  dadosNaoAssociado?: DadosNaoAssociado;
  modalidade?: ModalidadeParticipante;
  naoAssociado: boolean;
  eventoSistemico: boolean;
}

const INITIAL_STATE: RegistroReducerType = {
  flow: false,
  ferramenta: undefined,
  hibrido: false,
  loading: false,
  naoAssociado: false,
  eventoSistemico: false,
  idEvento: undefined,
  evento: undefined,
  dadosNaoAssociado: {
    checkTermo: true,
    nome: '',
  },
};

export const RegistroContext = createContext<RegistroContextType>({
  state: INITIAL_STATE,
  dispatch: () => {},
  searchCPF: (_cpf: string, _recaptchaToken: string) => Promise.reject(),
  changeAccountAttendance: (_value: boolean, _id: string) => {},
  confirmAssociatePresence: () => Promise.reject(),
  criarParticipanteNaoAssociado: () => Promise.reject(),
  isRecaptchaV2: false,
});

export default function RegistroProvider({
  children,
}: {
  children: JSX.Element;
}) {
  const [state, dispatch] = useReducer(RegistroReducer, INITIAL_STATE);
  const [recaptchaVersion, setRecaptchaVersion] = useState<'2' | '3'>('3');

  const { executeRecaptcha } = useGoogleReCaptcha();
  const { showToast } = useContext(LayoutContext);

  const navigate = useNavigate();

  const handleError = useCallback(
    (err: any) => {
      let errors: string[] = [];

      if (axios.isAxiosError(err)) {
        const serverError = err as AxiosError<ApiErrorType>;
        if (
          serverError.response?.data?.code === ApiErrorCode.RECAPTCHA_V3_ERROR
        ) {
          setRecaptchaVersion('2');
          errors = [languageValues.erros.recaptchaFallback];
        }
      }

      if (!errors.length) {
        errors = getErrorMessages(err);
      }

      showToastErrors(showToast, errors);
    },
    [showToast]
  );

  const obterEvento = useCallback(
    async (idEvento: number) => {
      const resp = await obterDadosEvento(
        idEvento,
        Ferramenta[state.ferramenta!]
      );
      dispatch({ type: 'setEvento', payload: resp.data });
      if (resp.data.agencias && resp.data.agencias.length === 1) {
        dispatch({
          type: 'setDadosNaoAssociado',
          payload: { agencia: resp.data.agencias[0].codAgencia },
        });
      }
    },
    [state.ferramenta]
  );

  const getToken = useCallback(
    async (recaptchaV2Token: string) => {
      if (!executeRecaptcha) throw new Error('ReCaptcha V3 não carregado');
      return recaptchaVersion === '2'
        ? recaptchaV2Token
        : await executeRecaptcha('homepage');
    },
    [executeRecaptcha, recaptchaVersion]
  );

  const searchCPF = useCallback(
    async (cpf: string, recaptchaV2Token: string) => {
      try {
        dispatch({ type: 'setLoading', payload: true });

        const token = await getToken(recaptchaV2Token);

        const resp = await buscarCPF(
          token,
          recaptchaVersion,
          cpf,
          state.idEvento,
          state.ferramenta,
          state.modalidade
        );

        switch (resp.status) {
          case 200:
            const data = resp.data as AssociadoType;
            dispatch({ type: 'setDados', payload: data });
            await obterEvento(state.idEvento!);
            dispatch({ type: 'setNaoAssociado', payload: true });
            dispatch({
              type: 'setEventoSistemico',
              payload: data.isEventoSistemico,
            });
            break;
          case 201:
            navigate(agradecimento, { replace: false });
            dispatch({ type: 'setModalidade', payload: undefined });
            break;
          case 202:
            dispatch({ type: 'setDados', payload: resp.data! });
            navigate(presenca, { replace: false });
            break;
          case 206:
            navigate(agradecimentoNaoAssociado, { replace: true });
            break;
          default:
            break;
        }
        return resp.data;
      } catch (err) {
        handleError(err);
      } finally {
        dispatch({ type: 'setLoading', payload: false });
      }
    },
    [
      getToken,
      handleError,
      navigate,
      obterEvento,
      recaptchaVersion,
      state.ferramenta,
      state.idEvento,
      state.modalidade,
    ]
  );

  const changeAccountAttendance = useCallback(
    (value: boolean, id: string) => {
      const index = state.dados?.accounts!?.findIndex((a) => a.id === id)!;
      if (index < 0) return;
      const d = { ...state.dados } as AssociadoType;
      d.accounts[index].flagAttendance = value;
      dispatch({ type: 'setDados', payload: d });
    },
    [state.dados]
  );

  const confirmAssociatePresence = useCallback(
    async (recaptchaV2Token: string) => {
      try {
        dispatch({ type: 'setLoading', payload: true });

        const token = await getToken(recaptchaV2Token);

        const resp = await confirmarPresencaAssociado(
          token,
          recaptchaVersion,
          state.dados!,
          state.modalidade,
          state.ferramenta
        );
        navigate(agradecimento, { replace: true });
        return resp;
      } catch (err) {
        handleError(err);
      } finally {
        dispatch({ type: 'setModalidade', payload: undefined });
        dispatch({ type: 'setLoading', payload: false });
      }
    },
    [
      getToken,
      recaptchaVersion,
      state.dados,
      state.modalidade,
      state.ferramenta,
      navigate,
      handleError,
    ]
  );

  const criarParticipanteNaoAssociado = useCallback(
    async (
      participante: ParticipanteNaoAssociado,
      recaptchaV2Token: string
    ) => {
      try {
        dispatch({ type: 'setLoading', payload: true });
        const token = await getToken(recaptchaV2Token);

        await salvarParticipanteNaoAssociado(
          token,
          recaptchaVersion,
          participante
        );
        dispatch({ type: 'setNaoAssociado', payload: false });
        dispatch({
          type: 'setDadosNaoAssociado',
          payload: { agencia: null, checkTermo: true, nome: '' },
        });
        dispatch({ type: 'setModalidade', payload: undefined });

        navigate(agradecimento, {
          replace: false,
        });
      } catch (e) {
        handleError(e);
      } finally {
        dispatch({ type: 'setLoading', payload: false });
      }
    },
    [getToken, recaptchaVersion, navigate, handleError]
  );

  return (
    <RegistroContext.Provider
      value={{
        state,
        dispatch,
        searchCPF,
        changeAccountAttendance,
        confirmAssociatePresence,
        criarParticipanteNaoAssociado,
        isRecaptchaV2: recaptchaVersion === '2',
      }}
    >
      {children}
    </RegistroContext.Provider>
  );
}
